import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { PageShell } from '../components/Layout';
import ScrollFade from '../components/ScrollFade';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { useFlowClient } from '../hooks/useFlowClient';
import { isAdminEmail } from '../utils/adminAllowlist';
import type { AdminEnvironment, AdminStep, AdminInviteResult, AdminInviteRow } from '../utils/flowClient';

type ProjectRole = 'PM' | 'Viewer';
type ProjectMode = 'none' | 'existing' | 'new';
type CompanyMode = 'existing' | 'new';
type ProjectOption = { project_id: string; project_name: string };
type Tab = 'invite' | 'requests';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const envLabel = (env: AdminEnvironment | null) => (env ? env.toUpperCase() : '…');

const Admin: React.FC = () => {
  const { userEmail } = useAuth();
  const { userMode } = useApp();
  const flowClient = useFlowClient();
  const [tab, setTab] = useState<Tab>('invite');
  const [environment, setEnvironment] = useState<AdminEnvironment | null>(null);
  const [envError, setEnvError] = useState<string | null>(null);

  // Not just visually hidden — the server independently enforces the same allowlist
  // against a verified JWT claim before running anything (see
  // server/src/routes/admin.ts's requireAdmin). This redirect only controls what a
  // non-admin's browser renders; it grants no access on its own.
  const defaultHome = '/portfolio';
  const isAdmin = isAdminEmail(userEmail);

  // The environment comes from the server's own deployment config — there is no way
  // to pick one here.
  useEffect(() => {
    if (!isAdmin || userMode === 'demo') return;
    flowClient.adminEnvironment()
      .then((r) => setEnvironment(r.environment))
      .catch((err) => setEnvError(err instanceof Error ? err.message : 'Could not determine environment.'));
  }, [flowClient, isAdmin, userMode]);

  if (userMode === 'demo' || !isAdmin) {
    return <Navigate to={defaultHome} replace />;
  }

  return (
    <PageShell className="mt-6 pb-20">
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <h1 className="h1-page dark:text-gray-100">Admin Center</h1>
          <EnvBadge environment={environment} />
        </div>
        <p className="text-sm text-muted dark:text-gray-400 mb-4">
          Grant real Flotillo access after personally corresponding with someone. Everything here acts on{' '}
          <strong>{envLabel(environment)}</strong> only.
        </p>
        {envError && <p className="text-xs text-crit-text bg-crit-bg px-3 py-2 rounded-[8px] mb-4">{envError}</p>}

        <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
          {(['invite', 'requests'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors tap ${
                tab === t ? 'border-accent text-accent dark:text-green-300 dark:border-green-300' : 'border-transparent text-muted hover:text-text dark:text-gray-400'
              }`}
            >
              {t === 'invite' ? 'Approve & Invite' : 'Requests'}
            </button>
          ))}
        </div>

        {environment && (tab === 'invite'
          ? <InviteForm environment={environment} />
          : <RequestsTab environment={environment} />)}
      
    </PageShell>
  );
};

const EnvBadge: React.FC<{ environment: AdminEnvironment | null }> = ({ environment }) => {
  if (!environment) return <span className="text-xs text-gray-400">checking environment…</span>;
  const prod = environment === 'production';
  return (
    <span
      className={`text-xs font-bold tracking-widest px-2.5 py-1 rounded-full border ${
        prod ? 'bg-crit-text text-white border-crit-text' : 'bg-watch-bg text-watch-text border-watch-border'
      }`}
      title="Derived from this server's deployment config"
    >
      {environment.toUpperCase()}
    </span>
  );
};

/* ─── Approve & Invite ─── */

interface SitePlan { siteUrl: string; state: 'empty' | 'partial' | 'complete'; totalLists: number; listsToCreate: string[]; listsPresent: string[] }

const InviteForm: React.FC<{ environment: AdminEnvironment }> = ({ environment }) => {
  const flowClient = useFlowClient();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [gcCompany, setGcCompany] = useState('');
  const [role, setRole] = useState<ProjectRole>('PM');
  const [companyMode, setCompanyMode] = useState<CompanyMode>('existing');
  const [siteUrl, setSiteUrl] = useState('');

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [projectsSite, setProjectsSite] = useState<{ siteUrl: string; registered: boolean } | null>(null);
  const [projectsForDomain, setProjectsForDomain] = useState<string | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectMode, setProjectMode] = useState<ProjectMode>('none');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [newProjectId, setNewProjectId] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState('');
  const [generatingId, setGeneratingId] = useState(false);

  const [checkingSite, setCheckingSite] = useState(false);
  const [sitePlan, setSitePlan] = useState<SitePlan | null>(null);
  const [showProvisionConfirm, setShowProvisionConfirm] = useState(false);
  const [showProdConfirm, setShowProdConfirm] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [liveSteps, setLiveSteps] = useState<AdminStep[]>([]);
  const [result, setResult] = useState<AdminInviteResult | null>(null);

  const running = !!jobId;
  const domain = EMAIL_RE.test(email.trim()) ? email.trim().toLowerCase().split('@')[1] : null;

  // Project IDs belong to one company site's counter/list. When the company (email
  // domain) or company mode changes, any selection made against the old site is
  // dropped so it can never carry over to a different site.
  const resetProjectSelection = () => {
    setProjectMode('none'); setSelectedProjectId(null); setNewProjectId(null); setNewProjectName('');
  };
  useEffect(() => {
    setProjectMode('none'); setSelectedProjectId(null); setNewProjectId(null); setNewProjectName('');
    setProjects([]); setProjectsSite(null); setProjectsForDomain(null);
  }, [domain, companyMode]);

  const loadProjects = useCallback(async () => {
    if (companyMode !== 'existing' || !domain || projectsForDomain === domain) return;
    setLoadingProjects(true);
    try {
      const res = await flowClient.adminListProjects(email.trim());
      setProjects(res.projects);
      setProjectsSite({ siteUrl: res.siteUrl, registered: res.registered });
      setProjectsForDomain(domain);
    } catch (err) {
      setProjects([]);
      setError(err instanceof Error ? err.message : 'Could not load projects.');
    } finally {
      setLoadingProjects(false);
    }
  }, [companyMode, domain, projectsForDomain, email, flowClient]);

  const startCreateNew = async () => {
    setProjectMode('new');
    setSelectedProjectId(null);
    setNewProjectId(null);
    setError(null);
    // A new company's site has no ID counter until it's provisioned, so its ID is
    // allocated server-side during the run.
    if (companyMode === 'new') return;
    if (!domain) { setError('Enter the invitee\'s email first.'); setProjectMode('none'); return; }
    setGeneratingId(true);
    try {
      const res = await flowClient.adminGenerateProjectId(email.trim());
      setNewProjectId(res.project_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate a new project ID.');
      setProjectMode('none');
    } finally {
      setGeneratingId(false);
    }
  };

  const reset = () => {
    setEmail(''); setName(''); setGcCompany(''); setRole('PM'); setCompanyMode('existing'); setSiteUrl('');
    resetProjectSelection(); setSitePlan(null);
    setError(null); setResult(null); setLiveSteps([]);
  };

  // Poll the server-side run until it finishes.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const job = await flowClient.adminJob(jobId);
        if (cancelled) return;
        setLiveSteps(job.steps);
        if (job.status === 'running') { timer = setTimeout(tick, 1500); return; }
        setJobId(null);
        if (job.status === 'done' && job.result) {
          setResult(job.result);
          if (job.result.projectCreated) setProjectsForDomain(null);
        } else {
          setError(job.error ?? 'The run failed.');
        }
      } catch (err) {
        if (cancelled) return;
        setJobId(null);
        setError(err instanceof Error ? err.message : 'Lost track of the run.');
      }
    };
    let timer = setTimeout(tick, 800);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [jobId, flowClient]);

  const submit = async () => {
    setShowProdConfirm(false);
    setShowProvisionConfirm(false);
    setError(null);
    setResult(null);
    setLiveSteps([]);
    try {
      const project_id = projectMode === 'existing' ? selectedProjectId ?? undefined
        : projectMode === 'new' && companyMode === 'existing' ? newProjectId ?? undefined
        : undefined;
      const project_name = projectMode === 'new' ? newProjectName.trim() || undefined : undefined;
      const res = await flowClient.approveAndInvite({
        email: email.trim(),
        name: name.trim(),
        role,
        project_id,
        project_name,
        generate_project_id: companyMode === 'new' && projectMode === 'new' ? true : undefined,
        gc_company: gcCompany.trim() || undefined,
        new_company_site_url: companyMode === 'new' ? siteUrl.trim() : undefined,
        confirmed_production: environment === 'production' ? true : undefined,
        confirmed_provision: companyMode === 'new' ? true : undefined,
      });
      setJobId(res.job_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  };

  const afterProvisionConfirmed = () => {
    setShowProvisionConfirm(false);
    if (environment === 'production') setShowProdConfirm(true);
    else submit();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !name.trim() || !gcCompany.trim()) {
      setError('Email, name, and company are required.');
      return;
    }
    if (!domain) { setError('Enter a valid email address.'); return; }
    if (projectMode === 'existing' && !selectedProjectId) {
      setError('Pick a project from the list, or choose "No project" / "Create new project".');
      return;
    }
    if (projectMode === 'new' && !newProjectName.trim()) {
      setError('Enter a project name for the new project (the ID is auto-generated).');
      return;
    }
    if (projectMode === 'new' && companyMode === 'existing' && !newProjectId) {
      setError('The new project ID has not been generated yet.');
      return;
    }
    if (companyMode === 'new') {
      if (!siteUrl.trim()) { setError('Enter the URL of the empty SharePoint site you created for this company.'); return; }
      setCheckingSite(true);
      try {
        const plan = await flowClient.adminCheckSite(email.trim(), siteUrl.trim());
        setSitePlan(plan);
        setShowProvisionConfirm(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not check the site.');
      } finally {
        setCheckingSite(false);
      }
      return;
    }
    if (environment === 'production') { setShowProdConfirm(true); return; }
    submit();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-5 space-y-3 max-w-xl">
        <div>
          <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1">Company</label>
          <div className="grid grid-cols-2 gap-1 bg-gray-50 dark:bg-gray-900 p-1 rounded-[8px]">
            {([['existing', 'Existing company'], ['new', 'New company (site not yet provisioned)']] as [CompanyMode, string][]).map(([m, label]) => (
              <button
                key={m}
                type="button"
                disabled={running}
                onClick={() => setCompanyMode(m)}
                className={`text-xs font-medium px-2 py-2 rounded-[6px] transition-colors ${
                  companyMode === m ? 'bg-white dark:bg-gray-700 shadow-sm text-accent dark:text-green-300' : 'text-muted hover:text-text dark:text-gray-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <TextField label="Email" type="email" required value={email} onChange={setEmail} onBlur={loadProjects} />
        <TextField label="Name" required value={name} onChange={setName} />
        <TextField label={companyMode === 'new' ? 'Company name' : 'Company / Tenant'} required value={gcCompany} onChange={setGcCompany} />
        <SelectField label="Role" value={role} onChange={(v) => setRole(v as ProjectRole)} options={['PM', 'Viewer']} />

        {companyMode === 'new' && (
          <div className="rounded-[8px] border border-watch-border/60 bg-watch-bg dark:bg-gray-900 p-3 space-y-2">
            <TextField label="Empty SharePoint site URL" required value={siteUrl} onChange={setSiteUrl} placeholder="https://pgpconstructionservices.sharepoint.com/sites/Flotillo-Acme" />
            <p className="text-xs text-muted dark:text-gray-400">
              Create the empty site in the SharePoint admin center first — that's the only manual step. Flotillo will
              check it, create every schema list on it, verify them, and register <strong>{domain ?? 'the email domain'}</strong>{' '}
              in {envLabel(environment)}'s TENANTS list before inviting anyone.
            </p>
          </div>
        )}

        <ProjectPicker
          projects={companyMode === 'existing' ? projects : []}
          allowExisting={companyMode === 'existing'}
          loading={loadingProjects}
          disabledReason={companyMode === 'existing' && !domain ? 'Enter the email first' : null}
          mode={projectMode}
          selectedProjectId={selectedProjectId}
          onOpen={loadProjects}
          onSelectExisting={(id) => { setProjectMode('existing'); setSelectedProjectId(id); }}
          onSelectNone={() => { setProjectMode('none'); setSelectedProjectId(null); }}
          onCreateNew={startCreateNew}
        />
        {companyMode === 'existing' && projectsSite && (
          <p className="text-[11px] text-gray-500 -mt-1">
            Company site: {projectsSite.siteUrl}{projectsSite.registered ? '' : ` (${domain} isn't in TENANTS yet — it will be added, pointing here)`}
          </p>
        )}

        {projectMode === 'new' && (
          <div className="rounded-[8px] border border-accent/20 bg-tint p-3 space-y-2">
            <p className="text-xs text-accent">
              <span className="font-semibold">New Project ID: </span>
              {companyMode === 'new' ? 'assigned once the new site is provisioned' : generatingId ? 'generating…' : newProjectId ?? '—'}
            </p>
            <TextField label="Project Name" required value={newProjectName} onChange={setNewProjectName} />
          </div>
        )}

        {error && <p className="text-xs text-crit-text bg-crit-bg px-3 py-2 rounded-[8px] whitespace-pre-wrap">{error}</p>}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={reset}
            disabled={running}
            className="flex-1 text-sm font-medium text-muted bg-gray-50 py-2.5 rounded-[8px] hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={running || generatingId || checkingSite}
            className="flex-1 text-sm font-semibold text-white bg-accent py-2.5 rounded-[8px] disabled:opacity-50 hover:bg-link transition-colors"
          >
            {checkingSite ? 'Checking site…' : running ? 'Running…' : 'Approve & Invite'}
          </button>
        </div>
      </form>

      {(running || result) && (
        <div className="mt-4 max-w-xl space-y-3">
          {result && <ResultBanner result={result} />}
          <StepList steps={result ? result.steps : liveSteps} />
        </div>
      )}

      {showProvisionConfirm && sitePlan && (
        <ConfirmDialog
          title={`Provision new company site on ${envLabel(environment)}`}
          tone={environment === 'production' ? 'danger' : 'normal'}
          confirmLabel={sitePlan.listsToCreate.length > 0 ? `Yes, create ${sitePlan.listsToCreate.length} lists` : 'Yes, continue'}
          onCancel={() => setShowProvisionConfirm(false)}
          onConfirm={afterProvisionConfirmed}
        >
          {sitePlan.state === 'empty' && (
            <>This will create <strong>{sitePlan.listsToCreate.length} lists</strong> on <strong className="break-all">{sitePlan.siteUrl}</strong> — confirm?</>
          )}
          {sitePlan.state === 'partial' && (
            <>
              {sitePlan.listsPresent.length} of {sitePlan.totalLists} lists already exist on <strong className="break-all">{sitePlan.siteUrl}</strong> from
              an earlier run. This will create the remaining <strong>{sitePlan.listsToCreate.length} lists</strong> and any missing columns
              (existing ones are left untouched) — confirm?
            </>
          )}
          {sitePlan.state === 'complete' && (
            <>All {sitePlan.totalLists} lists already exist on <strong className="break-all">{sitePlan.siteUrl}</strong>. Nothing new will be
              created unless a column is missing; the schema will be re-verified before inviting — continue?</>
          )}
          <span className="block mt-2">Then: register <strong>{domain}</strong> → this site in TENANTS, add the user/project/access rows, and send the invite.</span>
        </ConfirmDialog>
      )}

      {showProdConfirm && (
        <ConfirmDialog
          title="Confirm PRODUCTION write"
          tone="danger"
          confirmLabel="Yes, write to PRODUCTION"
          onCancel={() => setShowProdConfirm(false)}
          onConfirm={submit}
        >
          You are about to add <strong>{email.trim() || 'this user'}</strong> to <strong>PRODUCTION</strong>. This is
          real, live customer data. Continue?
        </ConfirmDialog>
      )}
    </>
  );
};

const ResultBanner: React.FC<{ result: AdminInviteResult }> = ({ result: r }) => {
  const [copied, setCopied] = useState(false);
  const env = r.environment.toUpperCase();
  const projectPart = r.projectId
    ? ` — project ${r.projectId}${r.projectName ? ` '${r.projectName}'` : ''}, role ${r.role}`
    : ' — no project (empty Portfolio)';
  const failedSteps = r.steps.filter((s) => s.status === 'failed');

  const copy = async () => {
    if (!r.inviteRedeemUrl) return;
    try { await navigator.clipboard.writeText(r.inviteRedeemUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };

  // "Sent" is shown ONLY when BuildAhead's own email send succeeded.
  if (r.ok && r.emailSent) {
    return (
      <div className="rounded-[8px] border border-accent/30 bg-tint px-4 py-3 text-sm text-accent">
        <p className="font-semibold">✓ Invitation sent to {r.email}{projectPart}.</p>
        <p className="text-xs mt-1">Environment: {env} · company {r.company}{r.inviteRecord === 'updated' ? ' · existing Requests row updated' : ''}</p>
      </div>
    );
  }

  if (r.inviteCreated && !r.emailSent) {
    return (
      <div className="rounded-[8px] border border-watch-border bg-watch-bg px-4 py-3 text-sm text-watch-text space-y-2">
        <p className="font-semibold">⚠ Invitation created for {r.email}{projectPart} — but the email was NOT sent ({env}).</p>
        <p className="text-xs">Reason: {r.emailError ?? 'unknown'}. Send this redeem link to them manually:</p>
        <div className="flex gap-2 items-center">
          <input readOnly value={r.inviteRedeemUrl ?? ''} className="flex-1 text-xs font-mono border border-watch-border rounded-[6px] px-2 py-1.5 bg-white" onFocus={(e) => e.target.select()} />
          <button type="button" onClick={copy} className="text-xs font-semibold text-white bg-watch-text px-3 py-1.5 rounded-[6px] hover:bg-watch-text">
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[8px] border border-crit-text/40 bg-crit-bg px-4 py-3 text-sm text-crit-text">
      <p className="font-semibold">✗ Not completed — no invitation was sent to {r.email} ({env}).</p>
      {failedSteps.map((s) => <p key={s.key} className="text-xs mt-1">Failed at "{s.label}": {s.detail}</p>)}
      <p className="text-xs mt-1">See below for exactly which steps completed. Every step is idempotent, so fixing the cause and re-submitting is safe.</p>
    </div>
  );
};

const STEP_ICON: Record<AdminStep['status'], string> = {
  pending: '○', running: '◌', done: '✓', failed: '✗', skipped: '–', not_run: '○',
};
const STEP_COLOR: Record<AdminStep['status'], string> = {
  pending: 'text-gray-400', running: 'text-watch-text animate-pulse', done: 'text-accent', failed: 'text-crit-text', skipped: 'text-gray-400', not_run: 'text-gray-400',
};

const StepList: React.FC<{ steps: AdminStep[] }> = ({ steps }) => (
  <div className="bg-white dark:bg-gray-800 rounded-[8px] border border-gray-100 dark:border-gray-700 p-4">
    <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Steps</p>
    {steps.length === 0 && <p className="text-xs text-gray-400">Starting…</p>}
    <ol className="space-y-1.5">
      {steps.map((s) => (
        <li key={s.key} className="text-xs flex gap-2">
          <span className={`w-4 shrink-0 font-bold ${STEP_COLOR[s.status]}`}>{STEP_ICON[s.status]}</span>
          <span className="min-w-0">
            <span className="font-medium text-text dark:text-gray-100">{s.label}</span>
            <span className="text-gray-500"> — {s.status === 'not_run' ? 'not run' : s.status}</span>
            {s.detail && <span className="block text-gray-500 break-words">{s.detail}</span>}
          </span>
        </li>
      ))}
    </ol>
  </div>
);

const ConfirmDialog: React.FC<{
  title: string; tone: 'normal' | 'danger'; confirmLabel: string; onCancel: () => void; onConfirm: () => void; children: React.ReactNode;
}> = ({ title, tone, confirmLabel, onCancel, onConfirm, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onCancel} role="dialog" aria-modal="true">
    <div className="bg-white dark:bg-gray-800 rounded-[12px] shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
      <h3 className={`text-lg font-semibold mb-2 ${tone === 'danger' ? 'text-crit-text' : 'text-text dark:text-gray-100'}`}>{title}</h3>
      <p className="text-sm text-muted dark:text-gray-400 mb-4">{children}</p>
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 text-sm font-medium text-muted bg-gray-50 py-2.5 rounded-[8px] hover:bg-gray-100 transition-colors">
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={`flex-1 text-sm font-semibold text-white py-2.5 rounded-[8px] transition-colors ${
            tone === 'danger' ? 'bg-crit-text hover:bg-crit-text' : 'bg-accent hover:bg-link'
          }`}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

/* ─── Requests tab ─── */

const STATUS_STYLE: Record<AdminInviteRow['status'], string> = {
  Sent: 'bg-blue-50 text-blue-800',
  'Pending acceptance': 'bg-watch-bg text-watch-text',
  Accepted: 'bg-tint text-accent',
  Failed: 'bg-crit-bg text-crit-text',
};

const RequestsTab: React.FC<{ environment: AdminEnvironment }> = ({ environment }) => {
  const flowClient = useFlowClient();
  const [rows, setRows] = useState<AdminInviteRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await flowClient.adminInvites();
      setRows(res.invites);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load invites.');
    } finally {
      setLoading(false);
    }
  }, [flowClient]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    setNote(null);
    try {
      const res = await flowClient.adminRefreshInvites();
      setRows(res.invites);
      if (res.permissionMissing) setNote(res.note);
      else setNote(`Checked ${res.checked} pending invite(s) in Entra; ${res.updated} status change(s).${res.errors.length ? ` ${res.errors.length} error(s): ${res.errors.join('; ')}` : ''}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not refresh statuses.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-[8px] shadow-sm border border-gray-100 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <p className="text-sm text-muted dark:text-gray-400">
          Every invitation sent from the {envLabel(environment)} Admin Center.
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={load} disabled={loading} className="text-xs font-medium text-muted bg-gray-50 px-3 py-1.5 rounded-[6px] hover:bg-gray-100 disabled:opacity-50">
            {loading ? 'Loading…' : 'Reload'}
          </button>
          <button type="button" onClick={refresh} disabled={refreshing} className="text-xs font-semibold text-white bg-accent px-3 py-1.5 rounded-[6px] hover:bg-link disabled:opacity-50">
            {refreshing ? 'Checking Entra…' : 'Refresh status'}
          </button>
        </div>
      </div>
      {note && <p className="text-xs text-watch-text bg-watch-bg px-3 py-2 rounded-[8px] mb-3">{note}</p>}
      {error && <p className="text-xs text-crit-text bg-crit-bg px-3 py-2 rounded-[8px] mb-3">{error}</p>}

      {/* Scrolls sideways on narrow screens; Email stays pinned on the left. */}
      <ScrollFade>
      <div className="overflow-x-auto overscroll-x-contain">
        <table className="w-full min-w-[720px] text-xs">
          <thead>
            <tr className="text-left text-muted uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
              <th className="sticky left-0 z-10 bg-white dark:bg-gray-800 py-2 pr-3 font-semibold w-[200px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">Email</th>
              <th className="py-2 pr-3 font-semibold">Status</th>
              <th className="py-2 pr-3 font-semibold">Company</th>
              <th className="py-2 pr-3 font-semibold">Project</th>
              <th className="py-2 pr-3 font-semibold">Role</th>
              <th className="py-2 font-semibold">Sent</th>
            </tr>
          </thead>
          <tbody>
            {rows?.map((r) => (
              <tr key={r.id} className="border-b border-gray-50 dark:border-gray-700 align-top">
                <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 py-2 pr-3 text-text dark:text-gray-100 break-all w-[200px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">{r.email}</td>
                <td className="py-2 pr-3">
                  <span className={`inline-block px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_STYLE[r.status] ?? ''}`}>{r.status}</span>
                  {r.status_detail && <span className="block text-[11px] text-gray-500 mt-0.5">{r.status_detail}</span>}
                </td>
                <td className="py-2 pr-3 text-text dark:text-gray-100">{r.company}</td>
                <td className="py-2 pr-3 text-text dark:text-gray-100">
                  {r.project_id ? <>{r.project_name || '—'} <span className="text-gray-500">({r.project_id})</span></> : <span className="text-gray-400">No project</span>}
                </td>
                <td className="py-2 pr-3 text-text dark:text-gray-100">{r.role || '—'}</td>
                <td className="py-2 text-text dark:text-gray-100 whitespace-nowrap">{r.sent_date ? new Date(r.sent_date).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows && rows.length === 0 && <p className="text-xs text-gray-400 py-4 text-center">No invitations sent from {envLabel(environment)} yet.</p>}
        {!rows && loading && <p className="text-xs text-gray-400 py-4 text-center">Loading…</p>}
      </div>
      </ScrollFade>
    </div>
  );
};

/* ─── ProjectPicker — searchable dropdown over existing projects, plus fixed
   "No project" and "+ Create new project" options ─── */

interface ProjectPickerProps {
  projects: ProjectOption[];
  allowExisting: boolean;
  loading: boolean;
  disabledReason: string | null;
  mode: ProjectMode;
  selectedProjectId: string | null;
  onOpen: () => void;
  onSelectExisting: (id: string) => void;
  onSelectNone: () => void;
  onCreateNew: () => void;
}

const ProjectPicker: React.FC<ProjectPickerProps> = ({
  projects, allowExisting, loading, disabledReason, mode, selectedProjectId, onOpen, onSelectExisting, onSelectNone, onCreateNew,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selectedProject = mode === 'existing' ? projects.find((p) => p.project_id === selectedProjectId) : null;
  const displayValue = mode === 'existing' && selectedProject
    ? `${selectedProject.project_id} — ${selectedProject.project_name}`
    : mode === 'new' ? '+ Create new project'
    : mode === 'none' ? 'No project (empty Portfolio)'
    : '';

  const filtered = projects.filter((p) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return p.project_id.toLowerCase().includes(q) || p.project_name.toLowerCase().includes(q);
  });

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1">Project</label>
      <div className="relative">
        <input
          type="text"
          value={open ? query : displayValue}
          disabled={!!disabledReason}
          onFocus={() => { setOpen(true); setQuery(''); onOpen(); }}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={disabledReason ?? (loading ? 'Loading projects…' : allowExisting ? 'Search by ID or name…' : '')}
          // Unfocused + still on the default "none" state reads as a placeholder-style
          // hint ("this is what happens if you don't pick anything"), not as an actual
          // selection someone already made for the admin.
          className={`w-full text-sm border border-gray-200 rounded-[8px] pl-3 pr-9 py-2 focus:outline-none focus:ring-2 focus:ring-accent disabled:bg-gray-50 ${
            !open && mode === 'none' ? 'text-gray-400' : 'text-text'
          }`}
        />
        {/* A custom text-input combobox, so it needs a dropdown arrow drawn explicitly. */}
        <svg
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {open && !disabledReason && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-[8px] shadow-lg max-h-64 overflow-y-auto">
          <button
            type="button"
            onClick={() => { onSelectNone(); setOpen(false); setQuery(''); }}
            className="w-full text-left px-3 py-2 text-sm text-muted hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            No project (empty Portfolio)
          </button>
          <button
            type="button"
            onClick={() => { onCreateNew(); setOpen(false); setQuery(''); }}
            className="w-full text-left px-3 py-2 text-sm font-semibold text-accent hover:bg-gray-50 dark:hover:bg-gray-700 border-t border-gray-100 dark:border-gray-700"
          >
            + Create new project
          </button>
          {allowExisting && (
            <div className="border-t border-gray-100 dark:border-gray-700">
              {filtered.length === 0 ? (
                <p className="px-3 py-2 text-xs text-gray-400">
                  {loading ? 'Loading…' : 'No matching projects.'}
                </p>
              ) : (
                filtered.map((p) => (
                  <button
                    key={p.project_id}
                    type="button"
                    onClick={() => { onSelectExisting(p.project_id); setOpen(false); setQuery(''); }}
                    className="w-full text-left px-3 py-2 text-sm text-text dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {p.project_id} — {p.project_name}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TextField: React.FC<{
  label: string; value: string; onChange: (v: string) => void; required?: boolean; type?: string; placeholder?: string; onBlur?: () => void;
}> = ({ label, value, onChange, required, type = 'text', placeholder, onBlur }) => (
  <div>
    <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1">
      {label}{required && ' *'}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      required={required}
      placeholder={placeholder}
      className="w-full text-sm border border-gray-200 rounded-[8px] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent tap"
    />
  </div>
);

const SelectField: React.FC<{ label: string; value: string; onChange: (v: string) => void; options: string[] }> = ({
  label, value, onChange, options,
}) => (
  <div>
    <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-sm border border-gray-200 rounded-[8px] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-accent tap"
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default Admin;
